import type { MaskitoPostprocessor, MaskitoPreprocessor } from "@maskito/core";

import { isAllZeros } from "./numberMaskUtils";

export interface TrailingDecimalHandlers {
  preprocessor: MaskitoPreprocessor;
  postprocessor: MaskitoPostprocessor;
}

/**
 * Creates processors to preserve trailing decimals during input.
 *
 * When `minimumFractionDigits` is 0, Maskito rejects trailing decimals ("123.").
 * This factory returns processors that temporarily append "0" to pass validation,
 * then trim it for display.
 */
export function createTrailingDecimalHandlers(): TrailingDecimalHandlers {
  /**
   * Appends "0" to trailing decimals so Maskito accepts them.
   * The postprocessor then trims this "0" for display.
   */
  const preprocessor: MaskitoPreprocessor = (
    { elementState, data },
    actionType,
  ) => {
    const { value, selection } = elementState;
    const [selStart, selEnd] = selection;

    // Typing "." that would result in trailing decimal
    if (data === "." && actionType === "insert") {
      const afterCursor = value.slice(selStart);
      if (!afterCursor || !/^\d/.test(afterCursor)) {
        return { elementState, data: ".0" };
      }
    }

    // Deletion that would result in trailing decimal
    if (actionType === "deleteBackward" || actionType === "deleteForward") {
      let simulatedValue: string;
      let newCursorPos: number;
      let charBeingDeleted: string;

      if (actionType === "deleteBackward") {
        if (selStart === selEnd && selStart > 0) {
          charBeingDeleted = value[selStart - 1];
          simulatedValue = value.slice(0, selStart - 1) + value.slice(selStart);
          newCursorPos = selStart - 1;
        } else {
          charBeingDeleted = value.slice(selStart, selEnd);
          simulatedValue = value.slice(0, selStart) + value.slice(selEnd);
          newCursorPos = selStart;
        }
      } else {
        if (selStart === selEnd && selStart < value.length) {
          charBeingDeleted = value[selStart];
          simulatedValue = value.slice(0, selStart) + value.slice(selStart + 1);
        } else {
          charBeingDeleted = value.slice(selStart, selEnd);
          simulatedValue = value.slice(0, selStart) + value.slice(selEnd);
        }
        newCursorPos = selStart;
      }

      // Append "0" unless user is explicitly deleting trailing zeros
      if (simulatedValue.endsWith(".")) {
        const decimalIndex = value.indexOf(".");
        const afterDecimal =
          decimalIndex !== -1 ? value.slice(decimalIndex + 1) : "";
        const isDeletingTrailingZeros =
          isAllZeros(afterDecimal) && isAllZeros(charBeingDeleted);

        if (!isDeletingTrailingZeros) {
          return {
            elementState: {
              value: simulatedValue + "0",
              selection: [newCursorPos, newCursorPos] as const,
            },
            data: "",
          };
        }
      }
    }

    return { elementState, data };
  };

  /**
   * Trims ".0" back to "." when the preprocessor added "0" to pass the mask.
   * Only applies when the initial value didn't already end with ".0".
   */
  const postprocessor: MaskitoPostprocessor = (
    { value, selection },
    initialElementState,
  ) => {
    const initialValue = initialElementState.value;

    if (value.endsWith(".0")) {
      const initialDecimalIndex = initialValue.indexOf(".");
      const hasDigitsAfterDecimal =
        initialDecimalIndex !== -1 &&
        initialValue.slice(initialDecimalIndex + 1).length > 0;

      if (!initialValue.includes(".") || hasDigitsAfterDecimal) {
        return {
          value: value.slice(0, -1),
          selection: [selection[0], selection[1]],
        };
      }
    }

    return { value, selection };
  };

  return {
    preprocessor,
    postprocessor,
  };
}
